import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Asset, Liability } from '../../types/buyerFinancialData';

interface AssetLiabilityChartProps {
  assets: Asset[];
  liabilities: Liability[];
  className?: string;
}

export function AssetLiabilityChart({ assets, liabilities, className = '' }: AssetLiabilityChartProps) {
  // Group assets by type
  const assetData = assets.reduce((acc, asset) => {
    const type = asset.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    if (!acc[type]) {
      acc[type] = 0;
    }
    acc[type] += asset.currentValue;
    return acc;
  }, {} as Record<string, number>);

  // Group liabilities by type
  const liabilityData = liabilities.reduce((acc, liability) => {
    const type = liability.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    if (!acc[type]) {
      acc[type] = 0;
    }
    acc[type] += liability.currentBalance;
    return acc;
  }, {} as Record<string, number>);

  // Combine data for chart
  const chartData = Object.keys({ ...assetData, ...liabilityData }).map(type => ({
    type,
    assets: assetData[type] || 0,
    liabilities: liabilityData[type] || 0,
    net: (assetData[type] || 0) - (liabilityData[type] || 0)
  }));

  // Calculate totals
  const totalAssets = assets.reduce((sum, asset) => sum + asset.currentValue, 0);
  const totalLiabilities = liabilities.reduce((sum, liability) => sum + liability.currentBalance, 0);
  const netWorth = totalAssets - totalLiabilities;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Assets:</span>
              <span className="font-semibold text-green-600">
                ${data.assets.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Liabilities:</span>
              <span className="font-semibold text-red-600">
                ${data.liabilities.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-4 pt-1 border-t border-gray-100">
              <span className="text-gray-600">Net:</span>
              <span className={`font-semibold ${data.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${data.net.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Assets vs Liabilities Breakdown</h3>
        <p className="text-sm text-gray-600">
          Detailed breakdown of assets and liabilities by category. Net worth is calculated as total assets minus total liabilities.
        </p>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="type" 
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            <Bar 
              dataKey="assets" 
              fill="#10b981" 
              name="Assets"
              radius={[2, 2, 0, 0]}
            />
            <Bar 
              dataKey="liabilities" 
              fill="#ef4444" 
              name="Liabilities"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Summary Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            ${(totalAssets / 1000).toFixed(0)}k
          </div>
          <div className="text-sm text-gray-600">Total Assets</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">
            ${(totalLiabilities / 1000).toFixed(0)}k
          </div>
          <div className="text-sm text-gray-600">Total Liabilities</div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-bold ${netWorth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            ${(netWorth / 1000).toFixed(0)}k
          </div>
          <div className="text-sm text-gray-600">Net Worth</div>
        </div>
      </div>
    </div>
  );
}

